import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { firstValueFrom, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, CustomerService, PartnerService, UserService } from '../services';
import { initialUserState, UserState } from '../models/state/user.state';
import { PageRequest, UserCreateUpdateRequest } from '../models';
import { HttpErrorResponse } from '@angular/common/http';

export const AdminUserStore = signalStore(
  { providedIn: 'root' },
  withState<UserState>(initialUserState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      userService = inject(UserService),
      customerService = inject(CustomerService),
      partnerService = inject(PartnerService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
    ) => {
      const loadUsers = rxMethod<PageRequest>(
        pipe(
          map((_pageRequest) => {
            const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
            patchState(store, { loading: true, pageRequest });
            return pageRequest;
          }),
          switchMap((pageRequest) =>
            userService.getUsersByRole(pageRequest).pipe(
              tap((users) => patchState(store, { users })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );
      const loadMoreUsers = rxMethod<void>(
        pipe(
          map(() => {
            const { page, ...restOfPageRequest } = store.pageRequest();
            const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { pageRequest });
            return pageRequest;
          }),
          concatMap((pageRequest) =>
            userService
              .getUsersByRole(pageRequest)
              .pipe(tap((users) => patchState(store, { users: [...store.users(), ...users] }))),
          ),
        ),
      );
      const loadUsersMetrics = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { metricsLoading: true })),
          switchMap(() =>
            userService.getUsersStatisticsByRole().pipe(
              tap((metrics) => patchState(store, { metrics })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { metricsLoading: false })),
            ),
          ),
        ),
      );
      const deleteUser = rxMethod<number>(
        pipe(
          switchMap((userId) =>
            userService.deleteUserByRole(userId).pipe(
              tap(() => {
                patchState(store, {
                  users: [...store.users().filter((_user) => _user.id !== userId)],
                });
                toastrService.success(translateService.instant('users.alerts.deleteSuccess'));
              }),
              catchError((error) => {
                toastrService.error(
                  translateService.instant(
                    error.status === 403 ? 'common.alerts.permissionDenied' : 'common.alerts.genericError',
                  ),
                );
                return of();
              }),
            ),
          ),
        ),
      );

      const loadAllCustomers = rxMethod<void>(
        switchMap(() =>
          customerService.getAllCustomersByRole().pipe(
            tap((customers) => patchState(store, { customers })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );
      const loadAllPartners = rxMethod<void>(
        switchMap(() =>
          partnerService.getAllPartners().pipe(
            tap((partners) => patchState(store, { partners })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );

      return {
        loadUsers,
        loadMoreUsers,
        loadUsersMetrics,
        deleteUser,
        loadAllCustomers,
        loadAllPartners,

        async createUser(userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const createdUser = await firstValueFrom(userService.createUserAsAdmin(userData));

            patchState(store, { users: [...store.users(), createdUser] });
            toastrService.success(translateService.instant('users.alerts.createSuccess'));
          } catch (error: HttpErrorResponse | any) {
            toastrService.error(
              translateService.instant(
                error.status === 409
                  ? 'users.alerts.emailAlreadyUsedError'
                  : error.status === 403
                    ? 'common.alerts.permissionDenied'
                    : 'common.alerts.genericError',
              ),
            );
          } finally {
            patchState(store, { saving: false });
          }
        },

        async updateUser(id: number, userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const updatedUser = await firstValueFrom(userService.updateUserAsAdmin(id, userData));
            patchState(store, {
              users: [...store.users().map((u) => (u.id === id ? { ...u, ...updatedUser } : u))],
            });
            toastrService.success(translateService.instant('users.alerts.updateSuccess'));
          } catch (error: HttpErrorResponse | any) {
            toastrService.error(
              translateService.instant(
                error.status === 409
                  ? 'users.alerts.emailAlreadyUsedError'
                  : error.status === 403
                    ? 'common.alerts.permissionDenied'
                    : 'common.alerts.genericError',
              ),
            );
          } finally {
            patchState(store, { saving: false });
          }
        },
      };
    },
  ),
);
